import { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { motion } from 'framer-motion';
import { useNoshStore, Recipe } from '../lib/store';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Heart, Clock, ChefHat, Flame, Filter, Search, Bot, ArrowRight, MapPin, Sparkles, X, ScanLine, ChevronDown, ChevronUp } from 'lucide-react';
// import { ThemeToggle } from '../components/ui/theme-toggle';
import { MealGenerationModal, MealGenerationOptions } from '../components/ui/meal-generation-modal';
import { IngredientSourceModal } from '../components/ui/ingredient-source-modal';
import { CachedImage } from '../components/ui/CachedImage';
import { getOptimizedImageUrl } from '../lib/image-optimization';
import { AIUsageService } from '../lib/aiUsageService';

import { cn, formatCalories } from '../lib/utils';

import { useMobileSwipeBack } from '../components/ui/mobile-swipe-back';
import { useLeftEdgeSwipeDisable } from '../hooks/useLeftEdgeSwipeDisable';
import { getHeroIngredients, calculateNutritionPercentages, getNutritionStatusColor } from '../components/ui/recipe-card-utils';
import { useMobileTouchHandler } from '../hooks/useMobileTouchHandler';

const Recipes = () => {
  const { 
    recipes, 
    inventory, 
    user,
    loadRecipes,
    isOnline,
    ingredientSource: storeIngredientSource,
    showIngredientSourceModal, 
    hideIngredientSourceModal,
    setSelectedIngredientSource,
    toggleFavoriteRecipe
  } = useNoshStore();

  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [showRecipeModal, setShowRecipeModal] = useState(false);
  const [showMealOptions, setShowMealOptions] = useState(false);
  
  
  // Filter dropdown state
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [sortByDate, setSortByDate] = useState<'latest' | 'oldest'>('latest');
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  
  // Tooltip state for AI button
  const [showGenerateTooltip, setShowGenerateTooltip] = useState(false);
  const [buttonRect, setButtonRect] = useState<DOMRect | null>(null);
  const generateButtonRef = useRef<HTMLButtonElement>(null);

  // Add scroll state for frosted glass effect
  const [isScrolled, setIsScrolled] = useState(false);
  const scrollContainerRef = useRef<HTMLElement | null>(null);
  const lastScrollTop = useRef(0);
  const scrollDirection = useRef<'up' | 'down' | null>(null);
  const scrollTimeout = useRef<NodeJS.Timeout | null>(null);

  // Pagination state for desktop performance optimization
  const [displayCount, setDisplayCount] = useState(9); // Initially show 9 recipes on desktop
  const LOAD_MORE_COUNT = 6; // Load 6 more each time

  // Mobile scroll-based loading state - per section
  const [sectionDisplayCounts, setSectionDisplayCounts] = useState<Record<string, number>>({});
  const MOBILE_INITIAL_COUNT = 5; // Show 5 cards initially per section
  const MOBILE_LOAD_MORE_COUNT = 5; // Load 5 more when scrolling

  // Add mobile swipe back functionality
  useMobileSwipeBack({
    enableSwipeBack: false, // COMPLETELY DISABLED to prevent accidental logout
    threshold: 100,
    edgeWidth: 50
  });

  // Prevent browser native left edge swipe gesture on mobile
  useLeftEdgeSwipeDisable({
    pageId: 'Recipes',
    enabled: true,
    edgeWidth: 50
  });

  // Scroll detection for frosted glass effect based on direction
  useEffect(() => {
    const scrollContainer = document.querySelector('[data-scroll-container]') as HTMLElement;
    scrollContainerRef.current = scrollContainer;
    
    if (!scrollContainer) return;

    const handleScroll = () => {
      const currentScrollTop = scrollContainer.scrollTop;
      const previousScrollTop = lastScrollTop.current;
      
      // Clear any existing timeout
      if (scrollTimeout.current) {
        clearTimeout(scrollTimeout.current);
      }
      
      // Determine scroll direction with debouncing
      if (currentScrollTop > previousScrollTop && currentScrollTop > 10) {
        // Scrolling down and past initial threshold
        if (scrollDirection.current !== 'down') {
          scrollDirection.current = 'down';
          setIsScrolled(true);
        }
      } else if (currentScrollTop < previousScrollTop) {
        // Scrolling up - immediately return to solid white (no debounce for up)
        if (scrollDirection.current !== 'up') {
          scrollDirection.current = 'up';
          setIsScrolled(false);
        }
      }
      
      // Update last scroll position
      lastScrollTop.current = currentScrollTop;
    };

    scrollContainer.addEventListener('scroll', handleScroll);
    return () => scrollContainer.removeEventListener('scroll', handleScroll);
  }, []);

  // Close filter dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showFilterDropdown) {
        const target = event.target as Element;
        const filterButton = target.closest('.relative');
        if (!filterButton) {
          setShowFilterDropdown(false);
        }
      }
    };

    if (showFilterDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showFilterDropdown]);

  // Update button position when tooltip shows
  useEffect(() => {
    if (showGenerateTooltip && generateButtonRef.current) {
      const updatePosition = () => {
        if (generateButtonRef.current) {
          setButtonRect(generateButtonRef.current.getBoundingClientRect());
        }
      };
      
      updatePosition();
      // Also update on scroll/resize to maintain correct position
      window.addEventListener('scroll', updatePosition);
      window.addEventListener('resize', updatePosition);
      
      return () => {
        window.removeEventListener('scroll', updatePosition);
        window.removeEventListener('resize', updatePosition);
      };
    }
  }, [showGenerateTooltip]);

  // Check data freshness on mount - App.tsx now handles initial loading
  useEffect(() => {
    // Recipes mounted
    
    // Only load data if missing (App.tsx handles initial loading)
    if (recipes.length === 0) {
      console.log('🔄 Recipes missing data, triggering load...');
      loadRecipes();
    }
  }, []); // Empty dependency array - only run on mount

  // Secondary effect to handle cases where recipes are empty but user might be authenticated
  useEffect(() => {
    const recheckRecipes = async () => {
      // If recipes are empty and we're online, try loading again after a short delay
      if (recipes.length === 0 && isOnline) {
        console.log('⚠️ Recipes empty but online - retrying load...');
        setTimeout(async () => {
          await loadRecipes();
        }, 1000); // Wait 1 second then retry
      }
    };

    recheckRecipes();
  }, [recipes.length, isOnline, loadRecipes]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const categories = [
    { id: 'all', name: 'All', icon: '🍽️' },
    { id: 'breakfast', name: 'Breakfast', icon: '🍳' },
    { id: 'lunch', name: 'Lunch', icon: '🥪' },
    { id: 'dinner', name: 'Dinner', icon: '🍲' },
    { id: 'dessert', name: 'Dessert', icon: '🍰' },
    { id: 'snack', name: 'Snack', icon: '🍿' },
  ];

  // Filter and sort recipes based on search query, category, favorites, and date
  const filteredRecipes = recipes
    .filter(recipe => {
      // Search filter
      const matchesSearch = searchQuery === '' || 
        recipe.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        recipe.ingredients.some(ingredient => {
          const ingredientName = typeof ingredient === 'string' ? ingredient : ingredient.name;
          return ingredientName.toLowerCase().includes(searchQuery.toLowerCase());
        });
      
      // Category filter
      const matchesCategory = selectedCategory === 'all' || (() => {
        // Check if recipe has the meal type in its tags
        if (recipe.tags && recipe.tags.some(tag => 
          tag.toLowerCase() === selectedCategory.toLowerCase()
        )) {
          return true;
        }
        
        // Fallback: check if recipe has mealType property that matches
        if (recipe.mealType && recipe.mealType.toLowerCase() === selectedCategory.toLowerCase()) {
          return true;
        }
        
        return false;
      })();
      
      // Favorites filter
      const matchesFavorites = !showFavoritesOnly || recipe.isFavorite;
      
      const passes = matchesSearch && matchesCategory && matchesFavorites;
      
      // Debug logging for search functionality
      if (searchQuery && searchQuery.length > 0) {
        console.log(`🔍 Search Debug - Recipe: "${recipe.title}"`);
        console.log(`🔍 Search Query: "${searchQuery}"`);
        console.log(`🔍 Title Match: ${recipe.title.toLowerCase().includes(searchQuery.toLowerCase())}`);
        console.log(`🔍 Ingredients: ${JSON.stringify(recipe.ingredients)}`);
        console.log(`🔍 Ingredients Match: ${recipe.ingredients.some(ingredient => {
          const ingredientName = typeof ingredient === 'string' ? ingredient : ingredient.name;
          return ingredientName.toLowerCase().includes(searchQuery.toLowerCase());
        })}`);
        console.log(`🔍 Overall Search Match: ${matchesSearch}`);
        console.log(`🔍 Category Match: ${matchesCategory}`);
        console.log(`🔍 Final Result: ${passes}`);
        console.log('---');
      }
      
      return passes;
    })
    .sort((a, b) => {
      // Sort by date based on sortByDate setting using actual createdAt timestamps
      if (sortByDate === 'latest') {
        // Latest first (newest to oldest) - same as Dashboard
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      } else {
        // Oldest first (oldest to newest)
        return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      }
    });

  // Handle ingredient source selection
  const handleIngredientSourceSelected = (sourceType: 'inventory' | 'mixed' | 'photo', photoIngredients?: string[]) => {
    console.log('🎯 [RECIPES] Ingredient source selected:', sourceType, photoIngredients);
    
    // CRITICAL DEBUG: Log what we received and what we're storing
    console.log('🚨 [RECIPES] CRITICAL DEBUG - handleIngredientSourceSelected called with:', {
      sourceType,
      photoIngredients,
      photoIngredientsCount: photoIngredients?.length || 0,
      photoIngredientsType: typeof photoIngredients,
      isArray: Array.isArray(photoIngredients)
    });
    
    // Store the selected source and detected ingredients
    setSelectedIngredientSource(sourceType, photoIngredients);
    
    // CRITICAL FIX: Wait for store to update before showing modal
    setTimeout(() => {
      const currentState = useNoshStore.getState();
      console.log('🚨 [RECIPES] CRITICAL DEBUG - Store state after setSelectedIngredientSource:', {
        selectedSource: currentState.ingredientSource.selectedSource,
        detectedIngredients: currentState.ingredientSource.detectedIngredients,
        detectedIngredientsCount: currentState.ingredientSource.detectedIngredients?.length || 0
      });
      
      // Show the meal generation modal only after store is updated
      setShowRecipeModal(true);
    }, 50); // Small delay to ensure store update completes
  };

  // Handle back from meal generation to ingredient source
  const handleMealGenerationBack = () => {
    console.log('🔙 [RECIPES] User wants to go back to ingredient source');
    
    // Close meal generation modal
    setShowRecipeModal(false);
    
    // Reopen ingredient source modal
    showIngredientSourceModal();
  };

  const handleRecipeGenerate = async (options: MealGenerationOptions) => {
    console.log('🎯 [RECIPES] handleRecipeGenerate called!');
    console.log('🎯 [RECIPES] Options received:', JSON.stringify(options, null, 2));
    console.log('🎯 [RECIPES] Detected ingredients:', storeIngredientSource.detectedIngredients);
    
    const { 
      generateRecipeWithAI, 
      generateRecipeFromPhoto,
      startRecipeGeneration,
      updateRecipeGenerationProgress,
      completeRecipeGeneration,
      setRecipeGenerationError,
      cancelRecipeGeneration,
      showRecipeResult
    } = useNoshStore.getState();
    
    console.log('🎯 [RECIPES] Store functions loaded, starting generation...');
    
    try {
      console.log('🔄 [RECIPES] Starting loading modal...');
      // Start the loading modal
      startRecipeGeneration();
      
      // Check if we have detected ingredients from photo
      const hasDetectedIngredients = storeIngredientSource.detectedIngredients && storeIngredientSource.detectedIngredients.length > 0;
      
      let result;
      if (hasDetectedIngredients) {
        console.log('📸 [RECIPES] Using photo-detected ingredients, calling generateRecipeFromPhoto...');
        console.log('📸 [RECIPES] Detected ingredients:', storeIngredientSource.detectedIngredients);
        
        // Call the photo-specific AI generation function
        result = await generateRecipeFromPhoto(storeIngredientSource.detectedIngredients!, options);
        console.log('📥 [RECIPES] generateRecipeFromPhoto result:', result);
      } else {
        console.log('🤖 [RECIPES] Using regular inventory, calling generateRecipeWithAI...');
        
        // Call the regular AI generation function
        result = await generateRecipeWithAI(options);
        console.log('📥 [RECIPES] generateRecipeWithAI result:', result);
      }
      
      if (result.success && result.recipe) {
        console.log('✅ [RECIPES] Recipe generated successfully!');
        console.log('✅ [RECIPES] Recipe name:', result.recipe.name);
        console.log('✅ [RECIPES] Recipe details:', {
          name: result.recipe.name,
          calories: result.recipe.calories,
          cookTime: result.recipe.cookTime,
          difficulty: result.recipe.difficulty,
          servings: result.recipe.servings,
          ingredientCount: result.recipe.ingredients?.length
        });
        console.log('📊 [RECIPES] Generation context:', result.context);
        console.log('🔄 [RECIPES] About to call showRecipeResult with:', {
          recipe: result.recipe,
          options: options
        });
        
        // Show the generated recipe with proper photo context
        console.log('🔄 [RECIPES] Calling showRecipeResult...');
        try {
          // Include detected ingredients in generation options for photo recipes
          const enhancedOptions = hasDetectedIngredients 
            ? { ...options, detectedIngredients: storeIngredientSource.detectedIngredients, source: 'photo' }
            : options;
          
          showRecipeResult(result.recipe, enhancedOptions);
          console.log('✅ [RECIPES] showRecipeResult call completed with enhanced options:', enhancedOptions);
        } catch (showError) {
          console.error('❌ [RECIPES] Error calling showRecipeResult:', showError);
          console.error('❌ [RECIPES] Recipe data that failed:', result.recipe);
          console.error('❌ [RECIPES] Options that failed:', options);
        }
      } else {
        console.error('❌ [RECIPES] Recipe generation failed:', result.error);
        setRecipeGenerationError(result.error || 'Recipe generation failed');
      }
      
    } catch (error) {
      console.error('❌ [RECIPES] Failed to generate recipe:', error);
      console.error('❌ [RECIPES] Error details:', {
        name: error instanceof Error ? error.name : 'Unknown',
        message: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined
      });
      const errorMessage = error instanceof Error ? error.message : 'Failed to generate recipe';
      setRecipeGenerationError(errorMessage);
    }
  };

  const clearSearch = () => {
    setSearchQuery('');
  };

  // Initialize section display counts
  useEffect(() => {
    if (isMobile && selectedCategory === 'all') {
      const initialCounts: Record<string, number> = {};
      categories.slice(1).forEach(category => {
        initialCounts[category.id] = MOBILE_INITIAL_COUNT;
      });
      setSectionDisplayCounts(initialCounts);
    }
  }, [isMobile, selectedCategory]); // Removed categories from dependencies since it's static

  // Ref for storing observers to cleanup properly
  const observersRef = useRef<Map<string, IntersectionObserver>>(new Map());

  // Callback for scroll trigger element observation
  const observeScrollTrigger = useCallback((element: HTMLElement | null) => {
    if (!element || !isMobile || selectedCategory !== 'all') return;

    const sectionId = element.getAttribute('data-section-id');
    const cardIndex = parseInt(element.getAttribute('data-card-index') || '0');
    if (!sectionId) return;

    // Only observe cards that are potential trigger points (every 3rd card: 2, 7, 12, 17, etc.)
    const triggerIndices = [2, 7, 12, 17, 22, 27, 32, 37, 42, 47]; // Support up to 50+ cards per section
    if (!triggerIndices.includes(cardIndex)) return;

    const observerKey = `${sectionId}-${cardIndex}`;

    // Clean up any existing observer for this specific trigger
    const existingObserver = observersRef.current.get(observerKey);
    if (existingObserver) {
      existingObserver.disconnect();
      observersRef.current.delete(observerKey);
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const entrySection = entry.target.getAttribute('data-section-id');
          const entryCardIndex = parseInt(entry.target.getAttribute('data-card-index') || '0');
          
          if (entrySection && triggerIndices.includes(entryCardIndex)) {
            observer.unobserve(entry.target); // Prevent multiple triggers from same card
            
            setSectionDisplayCounts(prev => {
              const currentCount = prev[entrySection] || MOBILE_INITIAL_COUNT;
              const newCount = currentCount + MOBILE_LOAD_MORE_COUNT;
              
              return {
                ...prev,
                [entrySection]: newCount
              };
            });
          }
        }
      });
    }, {
      threshold: 0.5,
      rootMargin: '0px 100px 0px 0px'
    });

    observer.observe(element);
    observersRef.current.set(observerKey, observer);
  }, [isMobile, selectedCategory]);

  // Cleanup observers on unmount or category change
  useEffect(() => {
    return () => {
      observersRef.current.forEach(observer => observer.disconnect());
      observersRef.current.clear();
    };
  }, [selectedCategory]);

  return (
    <div className={`max-w-6xl mx-auto ${isMobile ? 'pb-20' : ''}`}>
      {/* Ingredient Source Selection Modal */}
      <IngredientSourceModal
        isOpen={storeIngredientSource.showModal}
        onClose={hideIngredientSourceModal}
        onSourceSelected={handleIngredientSourceSelected}
        inventoryCount={inventory.length}
        isMobile={isMobile}
      />

      {/* Meal Generation Modal */}
      <MealGenerationModal
        isOpen={showRecipeModal}
        onClose={() => setShowRecipeModal(false)}
        onBack={handleMealGenerationBack}
        onGenerate={handleRecipeGenerate}
        inventoryCount={inventory.length}
        shoppingItemsCount={0} // TODO: Get actual shopping items count from store
        detectedIngredients={storeIngredientSource.detectedIngredients}
      />


      {/* Desktop Header - only show on desktop */}
      {!isMobile && (
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-heading font-bold">Recipes</h1>
        <div className="hidden md:flex gap-2 mr-4">
          {/* Fancy AI Generate Button with Tooltip */}
          <div className="relative">
            <Button 
              onClick={() => showIngredientSourceModal()}
              disabled={false} // Will be controlled by loading state later
              onMouseEnter={() => setShowGenerateTooltip(true)}
              onMouseLeave={() => setShowGenerateTooltip(false)}
              className="relative overflow-hidden bg-gradient-to-r from-purple-500 via-violet-600 to-purple-700 hover:from-purple-600 hover:via-violet-700 hover:to-purple-800 text-white border-0 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 group ai-scan-button"
              ref={generateButtonRef}
            >
              {/* Animated background shimmer */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000" />
              
              {/* Content */}
              <div className="relative flex items-center gap-2">
                <div className="relative">
                  <ChefHat size={18} className="relative z-10" />
                  {/* Sparkle effects */}
                  <div className="absolute -top-1 -right-1 w-2 h-2 bg-yellow-300 rounded-full ai-sparkle-1" />
                  <div className="absolute -bottom-1 -left-1 w-1.5 h-1.5 bg-pink-300 rounded-full ai-sparkle-2" />
                </div>
                <span className="font-medium">Generate with AI</span>
                <Sparkles size={14} className="animate-pulse text-yellow-200" />
              </div>
              
              {/* Glow effect */}
              <div className="absolute inset-0 rounded-md bg-gradient-to-r from-purple-400/20 via-violet-500/20 to-purple-600/20 blur-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 scale-110" />
            </Button>

            {/* Tooltip */}
            {showGenerateTooltip && buttonRect && ReactDOM.createPortal(
              <motion.div
                initial={{ opacity: 0, y: -10, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -10, scale: 0.95 }}
                transition={{ duration: 0.2, ease: "easeOut" }}
                style={{
                  position: 'fixed',
                  top: buttonRect.bottom + 8,
                  left: Math.max(16, Math.min(window.innerWidth - 288, buttonRect.left + (buttonRect.width / 2) - 144)),
                  zIndex: 10000
                }}
              >
                {/* Arrow pointing up - positioned relative to button center */}
                <div 
                  className="w-3 h-3 bg-white border-l border-t border-gray-200 transform rotate-45 absolute -top-1.5 shadow-sm"
                  style={{
                    left: Math.max(16, Math.min(272, buttonRect.left + (buttonRect.width / 2) - (Math.max(16, Math.min(window.innerWidth - 288, buttonRect.left + (buttonRect.width / 2) - 144)))))
                  }}
                ></div>
                
                {/* Tooltip content */}
                <div className="bg-white rounded-xl shadow-xl border border-gray-200 p-4 w-72 relative">
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 bg-gradient-to-r from-purple-500 to-violet-600 rounded-lg flex items-center justify-center">
                        <ChefHat size={12} className="text-white" />
                      </div>
                      <h3 className="font-semibold text-gray-900 text-sm">AI Recipe Generator</h3>
                    </div>
                    
                    <p className="text-gray-600 text-sm leading-relaxed">
                      Generate personalized recipes based on your current inventory and dietary preferences.
                    </p>
                    
                    <div className="space-y-2">
                      <p className="text-xs font-medium text-gray-700 mb-1">AI will consider:</p>
                      <div className="grid grid-cols-2 gap-1 text-xs text-gray-600">
                        <div className="flex items-center gap-1">
                          <span>📦</span>
                          <span>Your inventory</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <span>🕒</span>
                          <span>Expiring items</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <span>🍽️</span>
                          <span>Meal preferences</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <span>⏱️</span>
                          <span>Cook time</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>,
              document.body
            )}
          </div>
        </div>
        </div>
      )}

      {/* Mobile Search and Filter Card - Moved to Top */}
      {isMobile && (
        <div className="sticky top-0 z-50 transition-all duration-300">
          <div className="px-2 pt-4">
            <div className="bg-transparent rounded-2xl p-4">
              <div className="flex flex-col gap-3">
                {/* Search Bar with Filter Button */}
                <div className="flex gap-3 items-center">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                    <input
                      type="text"
                      placeholder="Search recipes..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className={cn(
                        "w-full pl-10 pr-10 py-3 rounded-xl text-gray-900 placeholder-gray-400 text-base focus:outline-none transition-all duration-300",
                        isScrolled 
                          ? cn(
                              "thick-glass-effect",
                              user.floatingBarTheme === 'dark' ? 'floating-bar-dark' : 'floating-bar-light'
                            )
                          : "bg-white shadow-sm"
                      )}
                    />
                    {searchQuery && (
                      <button
                        onClick={clearSearch}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-red-500 hover:text-red-700 transition-colors"
                      >
                        <X size={18} />
                      </button>
                    )}
                  </div>
                  {/* Inline Filter Button */}
                  <div className="relative">
                    <button
                      onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                      className={cn(
                        "flex items-center justify-center px-3 py-3 rounded-xl transition-all duration-300 text-gray-600",
                        isScrolled 
                          ? cn(
                              "thick-glass-effect",
                              user.floatingBarTheme === 'dark' ? 'floating-bar-dark' : 'floating-bar-light'
                            )
                          : "bg-white shadow-sm hover:bg-gray-100"
                      )}
                    >
                      <Filter size={18} />
                      {(showFavoritesOnly || sortByDate !== 'latest') && (
                        <div className="absolute -top-1 -right-1 w-2 h-2 bg-primary rounded-full"></div>
                      )}
                    </button>
                    
                    {/* Filter Dropdown - Mobile */}
                    {showFilterDropdown && (
                      <div className="absolute top-full right-0 mt-2 w-64 bg-white rounded-xl shadow-lg border border-gray-200 z-[100] overflow-hidden">
                        <div className="p-4 space-y-4">
                          <h3 className="font-semibold text-gray-900 text-sm">Filter & Sort</h3>
                          
                          {/* Sort by Date Toggle */}
                          <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-700">Sort by Date</label>
                            <button
                              onClick={() => setSortByDate(sortByDate === 'latest' ? 'oldest' : 'latest')}
                              className="w-full flex items-center justify-between p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors"
                            >
                              <span className="text-sm text-gray-900">
                                {sortByDate === 'latest' ? 'Latest First' : 'Oldest First'}
                              </span>
                              {sortByDate === 'latest' ? (
                                <ChevronDown size={16} className="text-gray-500" />
                              ) : (
                                <ChevronUp size={16} className="text-gray-500" />
                              )}
                            </button>
                          </div>
                          
                          {/* Show Favorites Toggle */}
                          <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-700">Favorites</label>
                            <button
                              onClick={() => setShowFavoritesOnly(!showFavoritesOnly)}
                              className={`w-full flex items-center justify-between p-3 rounded-lg transition-colors ${
                                showFavoritesOnly 
                                  ? 'bg-red-50 border border-red-200' 
                                  : 'bg-gray-50 hover:bg-gray-100'
                              }`}
                            >
                              <span className={`text-sm ${showFavoritesOnly ? 'text-red-700' : 'text-gray-900'}`}>
                                Show Favorites Only
                              </span>
                              <Heart 
                                size={16} 
                                className={showFavoritesOnly ? 'text-red-500 fill-current' : 'text-gray-500'} 
                              />
                            </button>
                          </div>
                          
                          {/* Filter Status */}
                          {(showFavoritesOnly || sortByDate !== 'latest') && (
                            <div className="pt-2 border-t border-gray-200">
                              <p className="text-xs text-gray-500">
                                Active filters: {showFavoritesOnly && 'Favorites'} {sortByDate !== 'latest' && 'Sort: Oldest'}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                {/* Recipe Category Tabs - Hidden on mobile */}
                <div className="hidden flex w-full bg-gray-50 rounded-xl p-1 gap-1">
                  {categories.map(category => (
                    <button
                      key={category.id}
                      onClick={() => setSelectedCategory(category.id)}
                      className={`flex-1 py-2 rounded-xl text-xs font-medium transition-colors ${
                        selectedCategory === category.id
                          ? 'bg-primary text-white shadow-sm' 
                          : 'bg-transparent text-gray-600 dark:text-gray-300'
                      }`}
                      style={{ transition: 'background 0.2s, color 0.2s' }}
                    >
                      <span>{category.icon} {category.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Desktop Search and Filter Section - Make this sticky */}
      {!isMobile && (
        <div className="sticky top-0 z-40">
          <div className={cn(
            "rounded-xl shadow-soft p-4 transition-all duration-300",
            isScrolled 
              ? cn(
                  "thick-glass-effect",
                  user.floatingBarTheme === 'dark' ? 'floating-bar-dark' : 'floating-bar-light'
                )
              : "bg-white"
          )}>
            {/* Filter Tabs */}
            <Tabs value={selectedCategory} onValueChange={(value) => setSelectedCategory(value)}>
              <TabsList className={cn(
                "w-full mb-4 grid grid-cols-3 lg:grid-cols-6 transition-all duration-300",
                isScrolled 
                  ? "bg-white/20 backdrop-blur-sm" 
                  : "bg-gray-50 dark:bg-gray-700"
              )}>
                {categories.map((category) => {
                  // Define unique colors for each meal type
                  const getActiveClasses = (categoryId: string) => {
                    switch (categoryId) {
                      case 'all':
                        return 'data-[state=active]:bg-slate-100 data-[state=active]:dark:bg-slate-600 data-[state=active]:text-slate-900 data-[state=active]:dark:text-slate-100';
                      case 'breakfast':
                        return 'data-[state=active]:bg-amber-100 data-[state=active]:dark:bg-amber-800 data-[state=active]:text-amber-900 data-[state=active]:dark:text-amber-100';
                      case 'lunch':
                        return 'data-[state=active]:bg-yellow-100 data-[state=active]:dark:bg-yellow-800 data-[state=active]:text-yellow-900 data-[state=active]:dark:text-yellow-100';
                      case 'dinner':
                        return 'data-[state=active]:bg-indigo-100 data-[state=active]:dark:bg-indigo-800 data-[state=active]:text-indigo-900 data-[state=active]:dark:text-indigo-100';
                      case 'dessert':
                        return 'data-[state=active]:bg-pink-100 data-[state=active]:dark:bg-pink-800 data-[state=active]:text-pink-900 data-[state=active]:dark:text-pink-100';
                      case 'snack':
                        return 'data-[state=active]:bg-green-100 data-[state=active]:dark:bg-green-800 data-[state=active]:text-green-900 data-[state=active]:dark:text-green-100';
                      default:
                        return 'data-[state=active]:bg-gray-100 data-[state=active]:dark:bg-gray-600 data-[state=active]:text-gray-900 data-[state=active]:dark:text-gray-100';
                    }
                  };

                  return (
                    <TabsTrigger 
                      key={category.id} 
                      value={category.id} 
                      className={`flex items-center gap-1 text-xs sm:text-sm font-medium transition-colors text-gray-600 dark:text-gray-300 ${getActiveClasses(category.id)}`}
                    >
                      <span>{category.icon}</span>
                      <span className="hidden sm:inline">{category.name}</span>
                      <span className="sm:hidden">{category.name}</span>
                    </TabsTrigger>
                  );
                })}
              </TabsList>

              {/* Desktop View Controls - Single Row Layout */}
              <div className="flex items-center gap-4 mb-4">
                {/* Recipe Count */}
                <div className="text-sm text-muted-foreground whitespace-nowrap">
                  {filteredRecipes.length} recipes
                  {searchQuery && (
                    <span className="ml-1 text-primary">
                      for "{searchQuery}"
                    </span>
                  )}
                </div>
                
                {/* Search Bar */}
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-muted" size={18} />
                  <input
                    type="text"
                    placeholder="Search recipes..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className={cn(
                      "w-full pl-10 pr-10 py-2 rounded-lg border focus:outline-none text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 transition-all duration-300",
                      isScrolled 
                        ? "bg-white/20 backdrop-blur-sm border-white/30" 
                        : "bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600"
                    )}
                  />
                  {searchQuery && (
                    <button
                      onClick={clearSearch}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-red-500 hover:text-red-700 transition-colors"
                    >
                      <X size={18} />
                    </button>
                  )}
                </div>
                
                {/* Filter Controls */}
                <div className="flex gap-2">
                  <div className="relative">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                      className={cn(
                        "hover:bg-accent relative transition-all duration-300",
                        isScrolled 
                          ? "bg-white/20 backdrop-blur-sm" 
                          : "bg-gray-50 dark:bg-gray-700"
                      )}
                    >
                      <Filter size={16} />
                      {(showFavoritesOnly || sortByDate !== 'latest') && (
                        <div className="absolute -top-1 -right-1 w-2 h-2 bg-primary rounded-full"></div>
                      )}
                    </Button>
                    
                    {/* Filter Dropdown - Desktop */}
                    {showFilterDropdown && (
                      <div className="absolute top-full right-0 mt-2 w-64 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-600 z-[100] overflow-hidden">
                        <div className="p-4 space-y-4">
                          <h3 className="font-semibold text-gray-900 dark:text-gray-100 text-sm">Filter & Sort</h3>
                          
                          {/* Sort by Date Toggle */}
                          <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Sort by Date</label>
                            <button
                              onClick={() => setSortByDate(sortByDate === 'latest' ? 'oldest' : 'latest')}
                              className="w-full flex items-center justify-between p-3 rounded-lg bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                            >
                              <span className="text-sm text-gray-900 dark:text-gray-100">
                                {sortByDate === 'latest' ? 'Latest First' : 'Oldest First'}
                              </span>
                              {sortByDate === 'latest' ? (
                                <ChevronDown size={16} className="text-gray-500 dark:text-gray-400" />
                              ) : (
                                <ChevronUp size={16} className="text-gray-500 dark:text-gray-400" />
                              )}
                            </button>
                          </div>
                          
                          {/* Show Favorites Toggle */}
                          <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Favorites</label>
                            <button
                              onClick={() => setShowFavoritesOnly(!showFavoritesOnly)}
                              className={`w-full flex items-center justify-between p-3 rounded-lg transition-colors ${
                                showFavoritesOnly 
                                  ? 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800' 
                                  : 'bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600'
                              }`}
                            >
                              <span className={`text-sm ${showFavoritesOnly ? 'text-red-700 dark:text-red-300' : 'text-gray-900 dark:text-gray-100'}`}>
                                Show Favorites Only
                              </span>
                              <Heart 
                                size={16} 
                                className={showFavoritesOnly ? 'text-red-500 fill-current' : 'text-gray-500 dark:text-gray-400'} 
                              />
                            </button>
                          </div>
                          
                          {/* Filter Status */}
                          {(showFavoritesOnly || sortByDate !== 'latest') && (
                            <div className="pt-2 border-t border-gray-200 dark:border-gray-600">
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                Active filters: {showFavoritesOnly && 'Favorites'} {sortByDate !== 'latest' && 'Sort: Oldest'}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </Tabs>
          </div>
        </div>
      )}

      {/* Recipe Cards Section - NO white container, display directly on gray background like Inventory */}
      <div className={`${isMobile && selectedCategory !== 'all' ? 'px-4' : ''}`}>
        <Tabs defaultValue="all" onValueChange={(value) => setSelectedCategory(value)}>
          {categories.map((category) => (
            <TabsContent key={category.id} value={category.id} className="space-y-6 mt-0">
              {filteredRecipes.length > 0 ? (
                isMobile ? (
                  // Mobile: Horizontal Scrolling Sections by Meal Type
                  selectedCategory === 'all' ? (
                    <div className="space-y-6">
                      {categories.slice(1).map((mealCategory) => {
                        const categoryRecipes = filteredRecipes.filter(recipe => {
                          if (recipe.tags && recipe.tags.some(tag => tag.toLowerCase() === mealCategory.id.toLowerCase())) {
                            return true;
                          }
                          if (recipe.mealType && recipe.mealType.toLowerCase() === mealCategory.id.toLowerCase()) {
                            return true;
                          }
                          return false;
                        });

                        if (categoryRecipes.length === 0) return null;

                        return (
                          <div key={mealCategory.id} className="space-y-3">
                            {/* Section Header */}
                            <div className="flex items-center justify-between px-4">
                              <div className="flex items-center gap-2">
                                <span className="text-xl">{mealCategory.icon}</span>
                                <h3 className="text-lg font-heading font-semibold text-foreground">
                                  {mealCategory.name}
                                </h3>
                                <span className="text-sm text-muted-foreground">
                                  ({categoryRecipes.length})
                                </span>
                              </div>
                              {categoryRecipes.length > 2 && (
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  className="text-primary text-xs touch-manipulation"
                                  style={{ minHeight: '44px', minWidth: '44px' }}
                                  onTouchStart={(e) => e.stopPropagation()}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setSelectedCategory(mealCategory.id);
                                  }}
                                >
                                  Show All
                                </Button>
                              )}
                            </div>

                            {/* Horizontal Scrolling Container with Lazy Loading - Full Width */}
                            <div className="relative">
                              <div 
                                className="flex gap-4 overflow-x-auto scrollbar-hide py-4 px-4"
                                style={{
                                  scrollSnapType: 'x mandatory',
                                  WebkitOverflowScrolling: 'touch'
                                }}
                              >
                                {categoryRecipes
                                  .slice(0, sectionDisplayCounts[mealCategory.id] || MOBILE_INITIAL_COUNT)
                                  .map((recipe, index) => {
                                    // Define trigger points for loading more (every 5 cards after initial 5)
                                    const triggerIndices = [2, 7, 12, 17, 22, 27, 32, 37, 42, 47];
                                    const shouldObserve = triggerIndices.includes(index);
                                    
                                    return (
                                      <div 
                                        key={recipe.id}
                                        className="flex-shrink-0 w-72 px-2"
                                        style={{ scrollSnapAlign: 'start' }}
                                        data-section-id={mealCategory.id}
                                        data-card-index={index}
                                        ref={shouldObserve ? observeScrollTrigger : undefined}
                                      >
                                        <RecipeCard 
                                          recipe={recipe} 
                                          onToggleFavorite={() => toggleFavoriteRecipe(recipe.id)}
                                          isMobile={true}
                                        />
                                      </div>
                                    );
                                  })}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    // Single category view - Mobile 2-cards-per-row layout
                    <div className="space-y-4 -mx-4">
                      {/* Back Button Header */}
                      <div className="flex items-center gap-3 px-4">
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="text-primary touch-manipulation"
                          style={{ minHeight: '44px' }}
                          onTouchStart={(e) => e.stopPropagation()}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setSelectedCategory('all');
                          }}
                        >
                          <ArrowRight className="h-4 w-4 mr-2 rotate-180" />
                          Back to All Recipes
                        </Button>
                      </div>

                      {/* Current Category Header */}
                      <div className="flex items-center gap-2 px-4">
                        <span className="text-xl">
                          {categories.find(cat => cat.id === selectedCategory)?.icon || '🍽️'}
                        </span>
                        <h3 className="text-lg font-heading font-semibold text-foreground">
                          {categories.find(cat => cat.id === selectedCategory)?.name || 'Recipes'}
                        </h3>
                        <span className="text-sm text-muted-foreground">
                          ({filteredRecipes.length})
                        </span>
                      </div>

                      {/* Mobile 2-cards-per-row grid */}
                      <div className="grid grid-cols-2 gap-3">
                        {filteredRecipes.map((recipe) => (
                          <div key={recipe.id} className="w-full">
                            <CompactRecipeCard 
                              recipe={recipe} 
                              onToggleFavorite={() => toggleFavoriteRecipe(recipe.id)}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )
                ) : (
                  // Desktop: Paginated layout for performance
                  <div className="space-y-6">
                    <div className="flex flex-wrap justify-center gap-3 sm:gap-4 md:gap-5 lg:gap-6 xl:gap-8 2xl:gap-10"
                    >
                      {filteredRecipes.slice(0, displayCount).map((recipe) => (
                        <RecipeCard 
                          key={recipe.id}
                          recipe={recipe} 
                          onToggleFavorite={() => toggleFavoriteRecipe(recipe.id)}
                          isMobile={false}
                        />
                      ))}
                    </div>
                    
                    {/* Load More Button - Desktop Only */}
                    {filteredRecipes.length > displayCount && (
                      <div className="flex justify-center pt-4">
                        <Button 
                          variant="outline" 
                          onClick={() => setDisplayCount(prev => prev + LOAD_MORE_COUNT)}
                          className="px-8 py-2"
                        >
                          Load More Recipes ({filteredRecipes.length - displayCount} remaining)
                        </Button>
                      </div>
                    )}
                  </div>
                )
              ) : (
                <div className="text-center py-8">
                  <p className="text-text-muted mb-4">
                    {searchQuery ? `No recipes found for "${searchQuery}"` : 'No recipes found'}
                  </p>
                  <Button onClick={() => showIngredientSourceModal()}>
                    <Flame size={18} className="mr-2" />
                    Generate Recipe
                  </Button>
                </div>
              )}
            </TabsContent>
          ))}
        </Tabs>
      </div>


    </div>
  );
};

const RecipeCard = ({ 
  recipe, 
  onToggleFavorite,
  isMobile
}: { 
  recipe: Recipe, 
  onToggleFavorite: () => void,
  isMobile?: boolean
}) => {
  const { showSavedRecipe } = useNoshStore();

  const handleRecipeClick = async () => {
    console.log('🍳 [Recipe Card] Opening saved recipe:', recipe.title);
    await showSavedRecipe(recipe);
  };

  // Simplified mobile touch handling to prevent re-renders
  const handleClick = isMobile ? (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    handleRecipeClick();
  } : handleRecipeClick;

  // Disable complex animations on mobile to prevent flashing
  const cardVariants = isMobile ? {} : {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    whileHover: { scale: 1.02 },
    whileTap: { scale: 0.98 }
  };

  const cardTransition = isMobile ? {} : { duration: 0.08, ease: "easeOut" };

  return (
    <motion.div
      {...cardVariants}
      transition={cardTransition}
      className="relative cursor-pointer transition-all duration-75 min-w-72 max-w-80 flex-1"
      onClick={handleClick}
      style={{ touchAction: 'manipulation' }}
    >
      {/* Food image positioned to "pop out" - Stable positioning */}
      <div className={`relative z-10 ${isMobile ? 'flex justify-center w-full -mb-32' : 'mx-auto w-75 h-62 -mb-36'}`} style={isMobile ? { transform: 'translateY(-16px)' } : undefined}>
        <div className={`${isMobile ? 'w-64 h-64' : 'w-full h-full'}`}>
          <CachedImage
            src={getOptimizedImageUrl(recipe.imageUrl, isMobile ? 'horizontal-card' : 'recipe-card', { 
              fallbackUrl: '/img/small 1.png',
              quality: isMobile ? 75 : 85 
            })}
            alt={recipe.title}
            className="w-full h-full rounded-2xl object-cover"
            loading="lazy"
            fallbackSrc="/img/small 1.png"
            preserveAspectRatio={true}
            aspectRatio="1/1"
          />
        </div>
      </div>
      
      {/* Card positioned behind the image - Stable layout */}
      <div className={`bg-white dark:bg-[#1d2839] rounded-2xl border border-gray-100 dark:border-slate-700/50 shadow-[0_4px_20px_rgb(0,0,0,0.08)] ${isMobile ? '-mt-16 pt-24 pb-4 px-4' : '-mt-16 pt-32 pb-3 px-4'}`}>
        <div className="text-center">
          <div className={`${isMobile ? 'h-12' : 'h-14'} flex items-center justify-center mb-2`}>
            <h3 className={`font-heading font-semibold text-foreground line-clamp-2 text-center leading-tight ${isMobile ? 'text-base' : 'text-lg'}`}>{recipe.title}</h3>
          </div>
          
          {/* Hero Ingredients - Simplified for mobile */}
          {(() => {
            const heroIngredients = getHeroIngredients(recipe);
            const categoryColors: Record<string, { bg: string; text: string; border: string }> = {
              'poultry': { bg: 'bg-orange-100 dark:bg-orange-900/40', text: 'text-orange-700 dark:text-orange-300', border: 'border-orange-800/30' },
              'meat': { bg: 'bg-red-100 dark:bg-red-900/40', text: 'text-red-700 dark:text-red-300', border: 'border-red-800/30' },
              'fish': { bg: 'bg-blue-100 dark:bg-blue-900/40', text: 'text-blue-700 dark:text-blue-300', border: 'border-blue-800/30' },
              'grains': { bg: 'bg-amber-100 dark:bg-amber-900/40', text: 'text-amber-700 dark:text-amber-300', border: 'border-amber-800/30' },
              'vegetables': { bg: 'bg-green-100 dark:bg-green-900/40', text: 'text-green-700 dark:text-green-300', border: 'border-green-800/30' },
              'dairy': { bg: 'bg-indigo-100 dark:bg-indigo-900/40', text: 'text-indigo-700 dark:text-indigo-300', border: 'border-indigo-800/30' },
              'default': { bg: 'bg-gray-100 dark:bg-gray-900/40', text: 'text-gray-700 dark:text-gray-300', border: 'border-gray-800/30' }
            };
            
            return (
              <div className="mb-3">
                <div className="flex items-center justify-center gap-2 mb-2">
                  {heroIngredients.map((ingredient: { name: string; emoji: string; category: string }, index: number) => {
                    const colors = categoryColors[ingredient.category] || categoryColors.default;
                    return (
                      <div key={`${ingredient.name}-${index}`} className={`flex items-center ${colors.bg} rounded-full px-2 py-1 border dark:${colors.border}`}>
                        <span className={`${isMobile ? 'text-xs' : 'text-xs'} mr-1`}>{ingredient.emoji}</span>
                        <span className={`${isMobile ? 'text-xs' : 'text-xs'} font-medium ${colors.text}`}>{ingredient.name}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}
          
          <div className={`flex items-center justify-center gap-3 text-muted-foreground ${isMobile ? 'text-sm mb-3' : 'text-sm mb-2'}`}>
            <span className="flex items-center">
              <span className="mr-1">⏱️</span>
              {recipe.time} min
            </span>
            <span className="flex items-center">
              <span className="mr-1">🔥</span>
              {formatCalories(recipe.calories)} cal
            </span>
          </div>
          
          {/* Simplified Nutrition Progress Bars for mobile */}
          {(() => {
            const nutrition = calculateNutritionPercentages(recipe);
            return (
              <div className={`${isMobile ? 'mb-4' : 'mb-3'}`}>
                <div className={`space-y-2 ${isMobile ? 'text-sm' : 'text-sm'}`}>
                  {/* Protein */}
                  <div className="flex items-center">
                    <div className="flex items-center w-16">
                      <div className="w-2 h-2 bg-blue-500 rounded-full mr-2"></div>
                      <span className="text-muted-foreground text-sm">Protein</span>
                    </div>
                    <div className="flex items-center flex-1 ml-3">
                      <div className={`bg-muted rounded-full overflow-hidden ${isMobile ? 'w-32 h-2' : 'w-32 h-2'}`}>
                                                  <motion.div
                            className="h-full bg-gradient-to-r from-blue-500 to-blue-600 rounded-full"
                            initial={{ width: "0%" }}
                            animate={{ width: `${Math.round(nutrition.protein.percentage)}%` }}
                            transition={{ duration: 1.2, delay: isMobile ? 0.2 : 0.2, ease: "easeOut" }}
                          />
                      </div>
                      <span className={`ml-2 font-medium text-foreground ${isMobile ? 'text-sm' : 'text-sm'}`}>
                        {nutrition.protein.value}g
                      </span>
                    </div>
                  </div>
                  
                  {/* Carbs */}
                  <div className="flex items-center">
                    <div className="flex items-center w-16">
                      <div className="w-2 h-2 bg-amber-500 rounded-full mr-2"></div>
                      <span className="text-muted-foreground text-sm">Carbs</span>
                    </div>
                    <div className="flex items-center flex-1 ml-3">
                      <div className={`bg-muted rounded-full overflow-hidden ${isMobile ? 'w-32 h-2' : 'w-32 h-2'}`}>
                                                  <motion.div
                            className="h-full bg-gradient-to-r from-amber-500 to-amber-600 rounded-full"
                            initial={{ width: "0%" }}
                            animate={{ width: `${Math.round(nutrition.carbs.percentage)}%` }}
                            transition={{ duration: 1.2, delay: isMobile ? 0.5 : 0.5, ease: "easeOut" }}
                          />
                      </div>
                      <span className={`ml-2 font-medium text-foreground ${isMobile ? 'text-sm' : 'text-sm'}`}>
                        {nutrition.carbs.value}g
                      </span>
                    </div>
                  </div>
                  
                  {/* Fat */}
                  <div className="flex items-center">
                    <div className="flex items-center w-16">
                      <div className="w-2 h-2 bg-purple-500 rounded-full mr-2"></div>
                      <span className="text-muted-foreground text-sm">Fat</span>
                    </div>
                    <div className="flex items-center flex-1 ml-3">
                      <div className={`bg-muted rounded-full overflow-hidden ${isMobile ? 'w-32 h-2' : 'w-32 h-2'}`}>
                                                  <motion.div
                            className="h-full bg-gradient-to-r from-purple-500 to-purple-600 rounded-full"
                            initial={{ width: "0%" }}
                            animate={{ width: `${Math.round(nutrition.fat.percentage)}%` }}
                            transition={{ duration: 1.2, delay: isMobile ? 0.8 : 0.8, ease: "easeOut" }}
                          />
                      </div>
                      <span className={`ml-2 font-medium text-foreground ${isMobile ? 'text-sm' : 'text-sm'}`}>
                        {nutrition.fat.value}g
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      </div>
    </motion.div>
  );
};

const CompactRecipeCard = ({ 
  recipe, 
  onToggleFavorite
}: { 
  recipe: Recipe, 
  onToggleFavorite: () => void
}) => {
  const { showSavedRecipe } = useNoshStore();

  const handleRecipeClick = async () => {
    console.log('🍳 [Compact Recipe Card] Opening saved recipe:', recipe.title);
    await showSavedRecipe(recipe);
  };

  // Simplified mobile touch handling (CompactRecipeCard is mobile-only)
  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    handleRecipeClick();
  };

  return (
    // Remove animations for mobile stability
    <div
      className="relative cursor-pointer transition-all duration-75 w-full"
      onClick={handleClick}
      style={{ touchAction: 'manipulation' }}
    >
      {/* Compact Food image */}
      <div className="relative z-10 flex justify-center w-full -mb-28" style={{ transform: 'translateY(-2px)' }}>
        <div className="w-52 h-52">
          <CachedImage
            src={getOptimizedImageUrl(recipe.imageUrl, 'horizontal-card', { 
              fallbackUrl: '/img/small 1.png',
              quality: 75  // Lower quality for faster mobile loading
            })}
            alt={recipe.title}
            className="w-full h-full rounded-xl object-cover"
            loading="lazy"
            fallbackSrc="/img/small 1.png"
            preserveAspectRatio={true}
            aspectRatio="1/1"
          />
        </div>
      </div>
      
      {/* Compact Card */}
      <div className="bg-white dark:bg-[#1d2839] rounded-xl border border-gray-100 dark:border-slate-700/50 shadow-[0_4px_20px_rgb(0,0,0,0.08)] -mt-12 pt-20 pb-3 px-3">
        <div className="text-center">
          {/* Compact Title */}
          <div className="h-10 flex items-center justify-center mb-2">
            <h3 className="font-heading font-semibold text-foreground line-clamp-2 text-center leading-tight text-sm">
              {recipe.title}
            </h3>
          </div>
          
          {/* Compact Hero Ingredients */}
          {(() => {
            const heroIngredients = getHeroIngredients(recipe).slice(0, 2); // Show only 2 ingredients
            const categoryColors: Record<string, { bg: string; text: string; border: string }> = {
              'poultry': { bg: 'bg-orange-100 dark:bg-orange-900/40', text: 'text-orange-700 dark:text-orange-300', border: 'border-orange-800/30' },
              'meat': { bg: 'bg-red-100 dark:bg-red-900/40', text: 'text-red-700 dark:text-red-300', border: 'border-red-800/30' },
              'fish': { bg: 'bg-blue-100 dark:bg-blue-900/40', text: 'text-blue-700 dark:text-blue-300', border: 'border-blue-800/30' },
              'grains': { bg: 'bg-amber-100 dark:bg-amber-900/40', text: 'text-amber-700 dark:text-amber-300', border: 'border-amber-800/30' },
              'vegetables': { bg: 'bg-green-100 dark:bg-green-900/40', text: 'text-green-700 dark:text-green-300', border: 'border-green-800/30' },
              'dairy': { bg: 'bg-indigo-100 dark:bg-indigo-900/40', text: 'text-indigo-700 dark:text-indigo-300', border: 'border-indigo-800/30' },
              'default': { bg: 'bg-gray-100 dark:bg-gray-900/40', text: 'text-gray-700 dark:text-gray-300', border: 'border-gray-800/30' }
            };
            
            return (
              <div className="mb-2">
                <div className="flex items-center justify-center gap-1 mb-2">
                  {heroIngredients.map((ingredient: { name: string; emoji: string; category: string }, index: number) => {
                    const colors = categoryColors[ingredient.category] || categoryColors.default;
                    return (
                      <div key={`${ingredient.name}-${index}`} className={`flex items-center ${colors.bg} rounded-full px-1.5 py-0.5 border dark:${colors.border}`}>
                        <span className="text-xs mr-1">{ingredient.emoji}</span>
                        <span className={`text-xs font-medium ${colors.text}`}>{ingredient.name}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}
          
          {/* Compact Time & Calories */}
          <div className="flex items-center justify-center gap-2 text-muted-foreground text-xs mb-2">
            <span className="flex items-center">
              <span className="mr-1">⏱️</span>
              {recipe.time} min
            </span>
            <span className="flex items-center">
              <span className="mr-1">🔥</span>
              {formatCalories(recipe.calories)} cal
            </span>
          </div>
          
          {/* Simplified Static Nutrition - Mobile optimized with no animations */}
          {(() => {
            const nutrition = calculateNutritionPercentages(recipe);
            return (
              <div className="space-y-1">
                {/* Protein */}
                <div className="flex items-center text-xs">
                  <div className="flex items-center w-12">
                    <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mr-1"></div>
                    <span className="text-muted-foreground">Pro</span>
                  </div>
                  <div className="flex items-center flex-1 ml-2">
                    <div className="bg-muted rounded-full overflow-hidden w-16 h-1.5">
                      <motion.div
                        className="h-full bg-gradient-to-r from-blue-500 to-blue-600 rounded-full"
                        initial={{ width: "0%" }}
                        animate={{ width: `${Math.round(nutrition.protein.percentage)}%` }}
                        transition={{ duration: 1.0, delay: 0.2, ease: "easeOut" }}
                      />
                    </div>
                    <span className="ml-1 font-medium text-foreground text-xs">
                      {nutrition.protein.value}g
                    </span>
                  </div>
                </div>
                
                {/* Carbs */}
                <div className="flex items-center text-xs">
                  <div className="flex items-center w-12">
                    <div className="w-1.5 h-1.5 bg-amber-500 rounded-full mr-1"></div>
                    <span className="text-muted-foreground">Carb</span>
                  </div>
                  <div className="flex items-center flex-1 ml-2">
                    <div className="bg-muted rounded-full overflow-hidden w-16 h-1.5">
                                             <motion.div
                         className="h-full bg-gradient-to-r from-amber-500 to-amber-600 rounded-full"
                         initial={{ width: "0%" }}
                         animate={{ width: `${Math.round(nutrition.carbs.percentage)}%` }}
                         transition={{ duration: 1.0, delay: 0.4, ease: "easeOut" }}
                       />
                    </div>
                    <span className="ml-1 font-medium text-foreground text-xs">
                      {nutrition.carbs.value}g
                    </span>
                  </div>
                </div>
                
                {/* Fat */}
                <div className="flex items-center text-xs">
                  <div className="flex items-center w-12">
                    <div className="w-1.5 h-1.5 bg-purple-500 rounded-full mr-1"></div>
                    <span className="text-muted-foreground">Fat</span>
                  </div>
                  <div className="flex items-center flex-1 ml-2">
                    <div className="bg-muted rounded-full overflow-hidden w-16 h-1.5">
                                             <motion.div
                         className="h-full bg-gradient-to-r from-purple-500 to-purple-600 rounded-full"
                         initial={{ width: "0%" }}
                         animate={{ width: `${Math.round(nutrition.fat.percentage)}%` }}
                         transition={{ duration: 1.0, delay: 0.6, ease: "easeOut" }}
                       />
                    </div>
                    <span className="ml-1 font-medium text-foreground text-xs">
                      {nutrition.fat.value}g
                    </span>
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      </div>
    </div>
  );
};

export default Recipes;
